import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Info, TrendingUp, TrendingDown } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api';

// PR-STEP2-4 — cross-project owner view. Profit per project, DIRECTIONAL.
// Clearly labelled as not-exact; true profit arrives with the Step 3 money
// layers. Owner top-ups also land in Step 3 (they need per-project cash).

interface OwnerItem {
  id: string;
  code: string;
  nameEn: string;
  revenueSar: number;
  fuelSar: number;
  maintenanceSar: number;
  expensesSar: number;
  costSar: number;
  profitSar: number;
}

function sar(n: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(n)) + ' SAR';
}

export default function OwnerViewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<OwnerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<{ directional: boolean; items: OwnerItem[] }>('/api/v1/owner/summary')
      .then((r) => { if (!cancelled) setItems(r.data.items); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={32} variant="mark" />
            <div className="font-display text-lg text-gradient-gold">
              {t('home.owner', { defaultValue: 'Owner view' })}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/home')} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            <span>{t('common.backHome', { defaultValue: 'Home' })}</span>
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto w-full px-6 py-10">
        {/* Directional warning — this view is NOT exact. */}
        <div className="mb-8 flex items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
          <Info className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="text-sm text-foreground">
            <div className="font-medium">
              {t('owner.directionalTitle', { defaultValue: 'Directional figures, not exact' })}
            </div>
            <div className="text-muted-foreground mt-1">
              {t('owner.directionalBody', {
                defaultValue:
                  'This month’s revenue minus the costs we can already attribute by project (fuel, maintenance, expenses). True per-project profit and owner top-ups arrive with the Step 3 money layers.',
              })}
            </div>
          </div>
        </div>

        {loading && (
          <p className="text-center text-sm text-muted-foreground py-12">
            {t('common.loading', { defaultValue: 'Loading…' })}
          </p>
        )}
        {error && (
          <p className="text-center text-sm text-rose-500 py-12">
            {t('owner.error', { defaultValue: 'Could not load the owner summary.' })}
          </p>
        )}

        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {items.map((it) => {
              const positive = it.profitSar >= 0;
              return (
                <div key={it.id} className="rounded-2xl border border-border bg-card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="font-display text-xl text-foreground">{it.nameEn}</div>
                    {positive
                      ? <TrendingUp className="h-5 w-5 text-emerald-500" />
                      : <TrendingDown className="h-5 w-5 text-rose-500" />}
                  </div>
                  <div className={`text-2xl font-semibold ${positive ? 'text-emerald-500' : 'text-rose-500'}`}>
                    {sar(it.profitSar)}
                  </div>
                  <div className="text-xs text-muted-foreground mb-4">
                    {t('owner.profitMtd', { defaultValue: 'directional profit, this month' })}
                  </div>
                  <dl className="space-y-1 text-sm">
                    <Row label={t('owner.revenue', { defaultValue: 'Revenue' })} value={sar(it.revenueSar)} />
                    <Row label={t('owner.fuel', { defaultValue: 'Fuel' })} value={`- ${sar(it.fuelSar)}`} muted />
                    <Row label={t('owner.maintenance', { defaultValue: 'Maintenance' })} value={`- ${sar(it.maintenanceSar)}`} muted />
                    <Row label={t('owner.expenses', { defaultValue: 'Expenses' })} value={`- ${sar(it.expensesSar)}`} muted />
                  </dl>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={muted ? 'text-muted-foreground' : 'text-foreground'}>{value}</dd>
    </div>
  );
}
